<template>
    <article
        class="fu-card"
        :class="[
            'fu-card--' + caseData.type,
            {
                'fu-card--busy': busy,
                'fu-card--muted': context === 'scheduled' || context === 'closed' || context === 'cancelled'
            }
        ]"
    >
        <div class="fu-card__status">
            <template v-if="context === 'due' || context === 'scheduled'">
                <span class="fu-kind" :class="'fu-kind--' + caseData.stage">{{ stageLabel(caseData.stage) }}</span>

                <span v-if="caseData.is_due" class="fu-when" :class="{ 'fu-when--overdue': overdueDays(caseData.due_at) > 0 }">
                    {{ __('Due') }} {{ formatDate(caseData.due_at) }}
                    <small v-if="overdueDays(caseData.due_at) > 0">
                        ({{ __(':days days overdue', { days: overdueDays(caseData.due_at) }) }})
                    </small>
                </span>
                <span v-else class="fu-when">
                    {{ __('Next') }}: {{ formatDate(caseData.due_at) }}
                    <small>({{ fromNow(caseData.due_at) }})</small>
                </span>
            </template>

            <template v-else>
                <span class="fu-kind fu-kind--type">{{ typeLabel(caseData.type) }}</span>

                <span v-if="context === 'complicated'" class="fu-when">
                    {{ __('Complicated since') }} {{ formatDate(caseData.complicated_at) }}
                </span>
                <span v-else-if="context === 'cancelled'" class="fu-when">
                    {{ __('Cancelled on') }} {{ formatDate(caseData.cancelled_at) }}
                </span>
                <span v-else-if="context === 'closed'" class="fu-when">
                    {{ caseData.type === 'registration' ? __('Resolved') : __('Activated') }} {{ formatDate(caseData.activated_at) }}
                </span>

                <span v-if="caseData.stage && !caseData.cancelled_at" class="fu-next">
                    {{ __('Next reminder') }}: {{ stageLabel(caseData.stage) }} · {{ formatDate(caseData.due_at) }}
                </span>
            </template>
        </div>

        <div class="fu-card__person">
            <strong class="fu-name">{{ (subject && subject.name) || '—' }}</strong>

            <template v-if="subject">
                <a v-if="subject.phone" :href="subject.phone_link ? 'tel:' + subject.phone_link : null" class="fu-contact">
                    <span class="material-icons-round">call</span>
                    <span>{{ subject.phone }}</span>
                    <small v-if="subject.kind === 'subscription' && !subject.phone_verified">({{ __('not verified') }})</small>
                </a>
                <a v-if="subject.email" :href="'mailto:' + subject.email" class="fu-contact">
                    <span class="material-icons-round">mail</span>
                    <span>{{ subject.email }}</span>
                </a>

                <ul class="fu-details">
                    <template v-if="subject.kind === 'prescription_form'">
                        <li>{{ __('Form submitted') }}: {{ formatDateTime(subject.submitted_at) }}</li>
                        <li v-if="subject.insurance_company">
                            {{ __('Insurance') }}: {{ subject.insurance_company }}
                            <span v-if="subject.insurance_id">({{ subject.insurance_id }})</span>
                        </li>
                        <li v-if="subject.prescription_number">{{ __('Prescription no.') }}: {{ subject.prescription_number }}</li>
                        <li v-if="subject.document_type">{{ __('Document') }}: {{ subject.document_type }}</li>
                        <li v-if="subject.patient">
                            {{ __('Linked patient') }}: {{ subject.patient.name }}
                            <span v-if="subject.patient.registered_at">({{ __('registered') }} {{ formatDate(subject.patient.registered_at) }})</span>
                        </li>
                        <li v-if="subject.message" class="fu-details__message">
                            {{ __('Message') }}: {{ subject.message }}
                        </li>
                    </template>

                    <template v-else>
                        <li>
                            {{ __('Code') }}: <code>{{ subject.code }}</code>
                            · {{ __('entered') }} {{ formatDateTime(subject.code_entered_at) }}
                        </li>
                        <li>
                            {{ __('Phone verified') }}: {{ yesNo(subject.phone_verified) }}
                            · {{ __('Onboarding finished') }}: {{ yesNo(subject.onboarding_finished) }}
                        </li>
                        <li v-if="subject.registered_at">{{ __('Registered') }}: {{ formatDateTime(subject.registered_at) }}</li>
                    </template>
                </ul>
            </template>

            <div v-if="visibleTags.length" class="fu-tags">
                <span v-for="tag in visibleTags" :key="tag" class="fu-tag" :class="'fu-tag--' + tag">
                    {{ tagLabel(tag) }}<small v-if="tag === 'sent_to_nurse'"> · {{ formatDate(caseData.sent_to_nurse_at) }}</small>
                </span>
            </div>
        </div>

        <div class="fu-card__note">
            <follow-up-note
                :case-id="caseData.id"
                :note="caseData.note"
                :updated-at="noteUpdatedAt(caseData)"
                @save="$emit('note', $event)"
            ></follow-up-note>
        </div>

        <div class="fu-card__actions">
            <VButton
                v-for="action in actions"
                :key="action"
                :label="actionLabel(action, caseData.type)"
                :variant="actionVariant(action)"
                :color="actionColor(action)"
                :disabled="busy"
                size="sm"
                full-width
                @onClick="$emit('action', { caseId: caseData.id, action: action, type: caseData.type })"
            />

            <button type="button" class="fu-history-toggle" @click="showHistory = true">
                <span class="material-icons-round">history</span>
                {{ __('history') }} ({{ caseData.events.length }})
            </button>
        </div>

        <follow-up-history-modal
            v-if="showHistory"
            :case-data="caseData"
            @close="showHistory = false"
        ></follow-up-history-modal>
    </article>
</template>

<script>
import followUpMixin from './followUpMixin'
import FollowUpNote from './FollowUpNote.vue'
import FollowUpHistoryModal from './FollowUpHistoryModal.vue'

export default {
    components: {
        FollowUpNote,
        FollowUpHistoryModal
    },
    mixins: [followUpMixin],
    props: {
        caseData: {
            type: Object,
            required: true
        },
        // which list the card is rendered in; decides the wording and the buttons
        context: {
            type: String,
            default: 'due',
            validator(value) {
                return ['due', 'scheduled', 'closed', 'complicated', 'cancelled'].indexOf(value) !== -1
            }
        },
        busy: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showHistory: false
        }
    },
    computed: {
        subject() {
            return this.caseData.subject
        },
        actions() {
            switch (this.context) {
                case 'due':
                case 'scheduled':
                    return this.caseData.follow_up_actions || []
                case 'complicated':
                    return this.caseData.complicated_actions || []
                case 'cancelled':
                    return this.caseData.cancelled_actions || []
                default:
                    return []
            }
        },
        visibleTags() {
            const hidden = {
                complicated: ['complicated'],
                cancelled: ['cancelled'],
                closed: ['activated']
            }[this.context] || []

            return (this.caseData.tags || []).filter((tag) => hidden.indexOf(tag) === -1)
        }
    }
}
</script>

<style scoped lang="less">
@primary: #5937b7;
@text: #676486;
@heading: #2c2857;
@muted: #b3b1c2;
@green: #00a671;
@red: #ff3e57;
@orange: #fdbd05;
@blue: #2ec9ff;

.fu-card {
    display: grid;
    // six columns: status 1, patient details 1, note 3, buttons 1; the details get a floor so
    // an insurance number never breaks onto a second line
    grid-template-columns: minmax(0, 1fr) minmax(250px, 1fr) minmax(0, 3fr) minmax(0, 1fr);
    column-gap: 20px;
    row-gap: 12px;
    align-items: start;
    margin-bottom: 12px;
    padding: 16px 20px;
    border: 1px solid rgba(102, 96, 121, 0.2);
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
    transition: opacity 0.15s ease;

    &--busy {
        opacity: 0.6;
        pointer-events: none;
    }

    &--muted {
        background: #fbfbfc;
        box-shadow: none;
    }

    &__status,
    &__person {
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-width: 0;
    }

    &__actions {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    // narrow screens: status and patient side by side, note and buttons across the full width
    // (the span has to be escaped, LESS reads 1 / -1 as a division)
    @media (max-width: 1320px) {
        grid-template-columns: 180px minmax(0, 1fr);

        &__note {
            grid-column: ~"1 / -1";
        }

        &__actions {
            grid-column: ~"1 / -1";
            flex-direction: row;
            flex-wrap: wrap;
        }
    }
}

.fu-kind {
    display: inline-block;
    align-self: flex-start;
    padding: 3px 10px;
    border-radius: 12px;
    background: fade(@primary, 10%);
    color: @primary;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;

    &--contact_3,
    &--recontact {
        background: fade(@orange, 18%);
        color: darken(@orange, 18%);
    }

    &--registration {
        background: fade(@blue, 15%);
        color: darken(@blue, 25%);
    }

    &--nurse_followup {
        background: fade(@green, 14%);
        color: @green;
    }

    &--type {
        background: #f3f3f6;
        color: @text;
    }
}

.fu-when {
    color: @heading;
    font-size: 13px;
    font-weight: 700;

    small {
        display: block;
        color: @muted;
        font-weight: 400;
    }

    &--overdue {
        color: @red;

        small {
            color: @red;
        }
    }
}

.fu-next {
    color: @text;
    font-size: 12px;
}

.fu-name {
    color: @heading;
    font-size: 15px;
    overflow-wrap: anywhere;
}

.fu-contact {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    overflow-wrap: anywhere;
    color: @primary;
    font-size: 13px;

    .material-icons-round {
        font-size: 16px;
    }

    small {
        color: @red;
    }
}

.fu-details {
    margin: 2px 0 0;
    padding: 0;
    list-style: none;
    color: @text;
    font-size: 12px;

    li {
        overflow-wrap: anywhere;
        white-space: normal;
    }

    code {
        padding: 1px 5px;
        border-radius: 4px;
        background: #f3f3f6;
        color: @heading;
        font-size: 12px;
    }

    &__message {
        white-space: normal;
    }

    &--missing {
        color: @red;
    }
}

.fu-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
}

.fu-tag {
    display: inline-block;
    padding: 2px 9px;
    border-radius: 10px;
    background: #f3f3f6;
    color: @text;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;

    small {
        font-weight: 400;
        text-transform: none;
    }

    &--expertsconnect {
        background: fade(@primary, 10%);
        color: @primary;
    }

    &--second_diagnosis {
        background: fade(@orange, 18%);
        color: darken(@orange, 18%);
    }

    &--sent_to_nurse {
        background: fade(@green, 14%);
        color: @green;
    }

    &--complicated {
        background: fade(@red, 12%);
        color: @red;
    }

    &--activated {
        background: fade(@green, 14%);
        color: @green;
    }

    &--cancelled {
        background: #e9e8ef;
        color: @muted;
    }
}

.fu-history-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 4px;
    padding: 4px 0;
    border: 0;
    background: none;
    color: @muted;
    font-family: inherit;
    font-size: 12px;
    cursor: pointer;

    .material-icons-round {
        font-size: 16px;
    }

    &:hover {
        color: @primary;
    }
}

</style>
